import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { PssAuthService } from '../../../services/pss-auth.service';

export interface HitBattle {
  battleDate: string;
  role: 'attack' | 'defense';
  won: boolean;
  isDraw: boolean;
  isStarBattle: boolean;
  starsWon: number;
  trophyChange: number;
  leagueType: string;
}

export interface HitTarget {
  opponentId: string;
  opponentName: string;
  opponentAlliance: string;
  winCount: number;
  lossCount: number;
  attackWins: number;
  defenseWins: number;
  totalStarsWon: number;
  totalTrophiesGained: number;
  lastWinDate: string | null;
  battles: HitBattle[];
  currentTrophies: number | null;
  currentTournamentStars: number | null;
  snapshotTrophies: number | null;
  snapshotTournamentStars: number | null;
  previousTournamentStars: number | null;
  previousSnapshotDate: string | null;
  lastSnapshotDate: string | null;
  // User-editable note fields
  isFavorite: boolean;
  myHpRemaining: number | null;
  opponentHpRemaining: number | null;
  notes: string;
  isCompleted: boolean;
}

interface HitListNote {
  opponentId: string;
  isFavorite: boolean;
  myHpRemaining: number | null;
  opponentHpRemaining: number | null;
  notes: string;
  isCompleted: boolean;
}

type SortColumn = 'opponentName' | 'winCount' | 'lossCount' | 'winRatio' | 'trophies' | 'stars' | 'totalStarsWon' | 'lastWinDate';

@Component({
  selector: 'app-hit-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hit-list.component.html',
  styleUrls: ['./hit-list.component.css'],
})
export class HitListComponent implements OnInit {
  @Input() userId = '';
  @Input() isModalMode = false;

  daysInput = 30;
  searchTerm = '';
  hideCompleted = false;
  starValueMin: number | null = null;
  starValueMax: number | null = null;
  loading = false;
  detailsLoading = false;
  error = '';

  targets: HitTarget[] = [];
  expandedOpponentId: string | null = null;

  sortColumn: SortColumn = 'winCount';
  sortDir: 'asc' | 'desc' = 'desc';

  // Per-opponent save state (object spread triggers Angular change detection)
  noteSaving: Record<string, boolean> = {};
  noteSaveOk: Record<string, boolean> = {};
  noteSaveError: Record<string, string> = {};
  noteOpponentIds = new Set<string>();



  constructor(
    private http: HttpClient,
    private auth: PssAuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const cacheKey = this.getQueryParam('cacheKey');
    if (cacheKey) {
      const cached = this.loadCachedHitList(cacheKey);
      if (cached) {
        this.userId = cached.userId;
        this.daysInput = cached.days;
        this.targets = this.sortTargets(cached.targets);
        this.loadNotes(); // refresh notes even for cached hitlist
        return;
      }
    }

    // In route mode, pull userId from the auth service
    if (!this.userId && this.auth.currentSession) {
      this.userId = this.auth.currentSession.userId;
    }
    if (!this.userId && !this.isModalMode) {
      this.router.navigate(['/']);
      return;
    }
    this.loadHitList();
  }

  loadHitList(): void {
    if (!this.userId) return;
    const days = Math.min(Math.max(Math.floor(this.daysInput) || 30, 1), 365);
    this.loading = true;
    this.error = '';
    this.targets = [];
    this.expandedOpponentId = null;

    const session = this.auth.currentSession;
    const accessToken = session?.accessToken ?? '';
    const server = session?.productionServer ?? '';

    this.http.get<{ targets: any[] }>(
      `/api/battles/hitlist?userId=${encodeURIComponent(this.userId)}&days=${days}&accessToken=${encodeURIComponent(accessToken)}&server=${encodeURIComponent(server)}`
    ).subscribe({
      next: res => {
        this.targets = this.sortTargets(
          (res.targets ?? []).map((t: any): HitTarget => ({
            ...t,
            isFavorite:          t.isFavorite          ?? false,
            myHpRemaining:       t.myHpRemaining        ?? null,
            opponentHpRemaining: t.opponentHpRemaining  ?? null,
            notes:               t.notes               ?? '',
            isCompleted:         t.isCompleted          ?? false,
          }))
        );
        this.loading = false;
        this.loadNotes();
      },
      error: err => {
        this.error = err?.error?.error ?? 'Failed to load hit list.';
        this.loading = false;
        this.loadNotes();
      },
    });
  }

  private loadNotes(): void {
    if (!this.userId) return;
    this.http.get<{ notes: HitListNote[] }>(
      `/api/battles/hitlist/notes?userId=${encodeURIComponent(this.userId)}`
    ).subscribe({
      next: res => {
        const notes = res.notes ?? [];
        this.noteOpponentIds = new Set(notes.map(n => n.opponentId));
        this.mergeNotes(notes);
      },
      error: () => {}, // non-fatal
    });
  }

  private mergeNotes(notes: HitListNote[]): void {
    const map = new Map(notes.map(n => [n.opponentId, n]));
    const existing = new Map(this.targets.map(t => [t.opponentId, t]));

    for (const n of notes) {
      const target = existing.get(n.opponentId);
      if (!target) {
        this.targets.push({
          opponentId: n.opponentId,
          opponentName: `User ${n.opponentId}`,
          opponentAlliance: '',
          winCount: 0,
          lossCount: 0,
          attackWins: 0,
          defenseWins: 0,
          totalStarsWon: 0,
          totalTrophiesGained: 0,
          lastWinDate: null,
          battles: [],
          currentTrophies: null,
          currentTournamentStars: null,
          snapshotTrophies: null,
          snapshotTournamentStars: null,
          previousTournamentStars: null,
          previousSnapshotDate: null,
          lastSnapshotDate: null,
          isFavorite: n.isFavorite,
          myHpRemaining: n.myHpRemaining,
          opponentHpRemaining: n.opponentHpRemaining,
          notes: n.notes ?? '',
          isCompleted: n.isCompleted,
        });
        continue;
      }

      target.isFavorite          = n.isFavorite;
      target.myHpRemaining       = n.myHpRemaining;
      target.opponentHpRemaining = n.opponentHpRemaining;
      target.notes               = n.notes;
      target.isCompleted         = n.isCompleted;
    }

    this.targets = this.sortTargets(this.targets);
  }

  get filteredTargets(): HitTarget[] {
    const term = this.searchTerm?.trim().toLowerCase();
    const min = this.starValueMin != null ? Math.max(0, Math.floor(this.starValueMin)) : null;
    const max = this.starValueMax != null ? Math.max(0, Math.floor(this.starValueMax)) : null;

    return this.targets.filter(t => {
      if (this.hideCompleted && t.isCompleted) return false;

      const matchesTerm = !term ||
        t.opponentName?.toLowerCase().includes(term)
        || t.opponentAlliance?.toLowerCase().includes(term)
        || t.opponentId.toLowerCase().includes(term);

      const value = this.starValue(t);
      const matchesStarMin = min === null || (value !== null && value >= min);
      const matchesStarMax = max === null || (value !== null && value <= max);
      const matchesStarRange = min === null && max === null ? true : (value !== null && matchesStarMin && matchesStarMax);

      return matchesTerm && matchesStarRange;
    });
  }

  toggleExpanded(t: HitTarget): void {
    this.expandedOpponentId = this.expandedOpponentId === t.opponentId ? null : t.opponentId;
  }

  hasHitListNote(t: HitTarget): boolean {
    return this.noteOpponentIds.has(t.opponentId);
  }

  removeFromHitList(t: HitTarget, event: Event): void {
    event.stopPropagation();
    if (!this.userId || !t.opponentId) return;
    this.http.delete(`/api/battles/hitlist/notes?userId=${encodeURIComponent(this.userId)}&opponentId=${encodeURIComponent(t.opponentId)}`)
      .subscribe({
        next: () => {
          this.noteOpponentIds.delete(t.opponentId);
          if (!t.battles || t.battles.length === 0) {
            this.targets = this.targets.filter(target => target.opponentId !== t.opponentId);
            if (this.expandedOpponentId === t.opponentId) {
              this.expandedOpponentId = null;
            }
          } else {
            t.isFavorite = false;
            t.myHpRemaining = null;
            t.opponentHpRemaining = null;
            t.notes = '';
            t.isCompleted = false;
            this.targets = this.sortTargets(this.targets);
          }
        },
        error: () => {},
      });
  }

  toggleFavorite(t: HitTarget, event: Event): void {
    event.stopPropagation();
    t.isFavorite = !t.isFavorite;
    this.targets = this.sortTargets(this.targets);
    this.postNote(t, { next: () => {}, error: () => {} });
  }

  saveNote(t: HitTarget): void {
    if (this.noteSaving[t.opponentId]) return;
    this.noteSaving   = { ...this.noteSaving,   [t.opponentId]: true };
    this.noteSaveOk   = { ...this.noteSaveOk,   [t.opponentId]: false };
    this.noteSaveError = { ...this.noteSaveError, [t.opponentId]: '' };

    this.postNote(t, {
      next: () => {
        this.noteSaving  = { ...this.noteSaving,  [t.opponentId]: false };
        this.noteSaveOk  = { ...this.noteSaveOk,  [t.opponentId]: true };
        setTimeout(() => {
          this.noteSaveOk = { ...this.noteSaveOk, [t.opponentId]: false };
        }, 2000);
      },
      error: () => {
        this.noteSaving   = { ...this.noteSaving,   [t.opponentId]: false };
        this.noteSaveError = { ...this.noteSaveError, [t.opponentId]: 'Save failed' };
      },
    });
  }

  private postNote(t: HitTarget, callbacks: { next: () => void; error: () => void }): void {
    this.http.post('/api/battles/hitlist/notes', {
      userId:               this.userId,
      opponentId:           t.opponentId,
      isFavorite:           t.isFavorite,
      myHpRemaining:        this.toNullableInt(t.myHpRemaining),
      opponentHpRemaining:  this.toNullableInt(t.opponentHpRemaining),
      notes:                t.notes ?? '',
      isCompleted:          t.isCompleted,
    }).subscribe(callbacks);
  }

  private toNullableInt(val: number | null | undefined): number | null {
    if (val === null || val === undefined || (val as unknown) === '') return null;
    const n = Math.round(Number(val));
    return Number.isNaN(n) ? null : n;
  }

  battleResultLabel(b: HitBattle): string {
    if (b.isDraw) return 'Draw';
    return b.won ? 'Win' : 'Loss';
  }

  sort(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = column === 'opponentName' ? 'asc' : 'desc';
    }
    this.targets = this.sortTargets(this.targets);
  }

  private sortTargets(list: HitTarget[]): HitTarget[] {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      // Favorites float to the top regardless of sort column
      if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
      // Completed rows sink to the bottom
      if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
      // Then sort by selected column
      switch (this.sortColumn) {
        case 'opponentName':
          return dir * a.opponentName.localeCompare(b.opponentName);
        case 'winCount':
          return dir * (a.winCount - b.winCount);
        case 'lossCount':
          return dir * (a.lossCount - b.lossCount);
        case 'winRatio':
          return dir * (this.winRatioNum(a) - this.winRatioNum(b));
        case 'trophies': {
          const ta = a.currentTrophies ?? a.snapshotTrophies ?? -1;
          const tb = b.currentTrophies ?? b.snapshotTrophies ?? -1;
          return dir * (ta - tb);
        }
        case 'stars': {
          return dir * ((this.starValue(a) ?? -1) - (this.starValue(b) ?? -1));
        }
        case 'totalStarsWon':
          return dir * (a.totalStarsWon - b.totalStarsWon);
        case 'lastWinDate': {
          const da = a.lastWinDate ?? '';
          const db = b.lastWinDate ?? '';
          return dir * da.localeCompare(db);
        }
        default:
          return 0;
      }
    });
  }

  winRatioNum(t: HitTarget): number {
    const total = t.winCount + t.lossCount;
    return total > 0 ? t.winCount / total : 0;
  }

  winRatioLabel(t: HitTarget): string {
    return (this.winRatioNum(t) * 100).toFixed(0) + '%';
  }

  hasLostMoreThanWon(t: HitTarget): boolean {
    return t.lossCount > t.winCount;
  }

  hasMixedResults(t: HitTarget): boolean {
    return t.winCount > 0 && t.lossCount > 0 && t.lossCount <= t.winCount;
  }

  currentStars(t: HitTarget): number | null {
    if (t.currentTournamentStars != null && t.currentTournamentStars > 0) {
      return t.currentTournamentStars;
    }
    if (t.snapshotTournamentStars != null) {
      return t.snapshotTournamentStars;
    }
    return t.currentTournamentStars != null ? t.currentTournamentStars : null;
  }

  starValue(t: HitTarget): number | null {
    const priorStars  = t.previousTournamentStars;
    const trophies    = t.currentTrophies ?? t.snapshotTrophies;
    const starValue   = priorStars != null ? Math.floor(priorStars * 0.15) : null;
    const trophyValue = trophies != null ? Math.floor(trophies / 1000) : null;
    if (starValue == null && trophyValue == null) return null;
    if (starValue == null) return trophyValue;
    if (trophyValue == null) return starValue;
    return Math.max(starValue, trophyValue);
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  sortIndicator(column: SortColumn): string {
    if (this.sortColumn !== column) return '';
    return this.sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  openInNewTab(): void {
    if (!this.userId || this.targets.length === 0) return;

    const days = Math.min(Math.max(Math.floor(this.daysInput) || 30, 1), 365);
    const cacheKey = `hitlist-cache-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const payload = {
      userId: this.userId,
      days,
      targets: this.targets,
      cachedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(cacheKey, JSON.stringify(payload));
      window.open(`/#/hitlist?cacheKey=${encodeURIComponent(cacheKey)}`, '_blank');
    } catch {
      window.open(`/#/hitlist?days=${days}`, '_blank');
    }
  }

  private getQueryParam(name: string): string | null {
    try {
      const params = new URLSearchParams(window.location.search);
      const value = params.get(name);
      if (value) return value;
      const hashIndex = window.location.hash.indexOf('?');
      if (hashIndex === -1) return null;
      const hashQuery = window.location.hash.slice(hashIndex + 1);
      return new URLSearchParams(hashQuery).get(name);
    } catch {
      return null;
    }
  }

  private loadCachedHitList(cacheKey: string): { userId: string; days: number; targets: HitTarget[] } | null {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.userId !== 'string') return null;
      if (this.userId && parsed.userId !== this.userId) return null;
      if (!Array.isArray(parsed.targets) || typeof parsed.days !== 'number') return null;
      const ageMs = Date.now() - Date.parse(parsed.cachedAt || '');
      if (Number.isNaN(ageMs) || ageMs > 5 * 60 * 1000) return null;
      return {
        userId: parsed.userId,
        days: parsed.days,
        // Apply note-field defaults for caches created before this feature
        targets: parsed.targets.map((t: any): HitTarget => ({
          ...t,
          isFavorite:          t.isFavorite          ?? false,
          myHpRemaining:       t.myHpRemaining        ?? null,
          opponentHpRemaining: t.opponentHpRemaining  ?? null,
          notes:               t.notes               ?? '',
          isCompleted:         t.isCompleted          ?? false,
        })),
      };
    } catch {
      return null;
    }
  }
}
